import React, { useState, useRef, memo } from 'react';
import { Download, Upload } from 'lucide-react';
import { JoinSessionModal } from './join_session_modal.jsx';
import { SessionInformation } from '../utils_components.jsx';
import { ClipboardText } from './clipboard.jsx';
import { DawidConfirm } from './confirm.jsx';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from './ui/dialog';
import { Input } from './ui/input';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';
import { cn } from '../lib/utils';

function download(filename, text) {
  const element = document.createElement('a');
  element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
  element.setAttribute('download', filename);
  element.style.display = 'none';
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
}

// eslint-disable-next-line no-extend-native
Date.prototype.addDays = function (days) {
  const date = new Date(this.valueOf());
  date.setDate(date.getDate() + days);
  return date;
};

function dateToString(date) {
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  return year + '-' + month + '-' + day;
}

function DawidButtonsBar({
  clientConnected,
  sessionCreated,
  exportState,
  importState,
  createSession,
  joinSession,
  sessionIdPublic,
  sessionIdPrivate,
  mode,
  expiration,
  emailAddress,
  deleteSession,
  submitEmail,
}) {
  const [emailAddressFieldValue, setEmailAddressFieldValue] = useState(emailAddress);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const fileInputRef = useRef(null);

  const fileChanged = (e) => {
    const fileToUpload = e.target.files[0];
    const reader = new FileReader();
    reader.onload = (event) => {
      const sessionToImport = JSON.parse(event.target.result);
      importState(sessionToImport);
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsText(fileToUpload);
  };

  const labelConnection = clientConnected ? 'connected' : 'not connected';
  const privateLink = sessionIdPublic + '#' + sessionIdPrivate;
  const bannerVisible = sessionCreated && mode === 'editor';
  const expirationDays = Math.floor(expiration / 60 / 60 / 24);
  const dateObj = new Date();
  const today = dateToString(dateObj);
  const exportFileName = 'session_' + today + '.json';
  const expirationDate = dateToString(dateObj.addDays(expirationDays));

  return (
    <TooltipProvider>
      <div>
        {bannerVisible && (
          <Alert variant="info" className="mb-3">
            <AlertTitle>Keep the following identifier to be able to return to your run later</AlertTitle>
            <AlertDescription><ClipboardText value={privateLink} /></AlertDescription>
          </Alert>
        )}

        {expiration && bannerVisible && (
          <Alert variant="warning" className="mb-3">
            <AlertTitle>This session will expire on {expirationDate}</AlertTitle>
            <AlertDescription>
              {!emailAddress && (
                <>
                  <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
                    <DialogTrigger asChild>
                      <a href="#" className="link cursor-pointer" onClick={(e) => { e.preventDefault(); setEmailModalOpen(true); }}>
                        Click here to be notified by email before it gets deleted.
                      </a>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Notification before session deletion</DialogTitle>
                      </DialogHeader>
                      <DialogDescription asChild>
                        <div className="px-6 py-4 space-y-4">
                          <div className="flex items-center gap-3">
                            <label className="text-sm font-medium shrink-0">Your email address</label>
                            <Input
                              type="email"
                              value={emailAddressFieldValue}
                              onChange={(e) => setEmailAddressFieldValue(e.target.value)}
                            />
                          </div>
                          <Button onClick={() => { submitEmail(emailAddressFieldValue); setEmailModalOpen(false); }}>
                            Submit
                          </Button>
                        </div>
                      </DialogDescription>
                    </DialogContent>
                  </Dialog>
                </>
              )}
              {emailAddress && (
                <p>
                  You will be notified at{' '}
                  <span className="text-danger-900 font-medium">{emailAddress}</span>{' '}
                  before it gets deleted.
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}

        <div id="buttons_bar" className="w-full flex items-start justify-between mb-6 gap-2 flex-wrap">
          <input onChange={fileChanged} ref={fileInputRef} type="file" hidden />

          <div className="flex flex-wrap gap-2 items-center">
            {mode === 'editor' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => download(exportFileName, exportState())}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Export your current session</TooltipContent>
              </Tooltip>
            )}

            {mode === 'editor' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    <Upload className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Import a session</TooltipContent>
              </Tooltip>
            )}

            {!sessionCreated && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button id="create-session" variant="outline" onClick={(e) => createSession(e)}>
                    Create remote session
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  This will save your progress on our server and will allow sharing the session with observers
                </TooltipContent>
              </Tooltip>
            )}

            {!sessionCreated && (
              <JoinSessionModal
                popUpText="To resume a previous run or join someone else run as observer"
                onSubmit={(value) => joinSession(value)}
              />
            )}

            {sessionCreated && (
              <SessionInformation sessionIdPublic={sessionIdPublic} sessionIdPrivate={sessionIdPrivate} role={mode} />
            )}

            {sessionCreated && mode === 'editor' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button id="delete-session" variant="destructive" onClick={() => setDeleteConfirmOpen(true)}>
                    Delete session
                  </Button>
                </TooltipTrigger>
                <TooltipContent>This will delete your session from our server.</TooltipContent>
              </Tooltip>
            )}
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                id="ws-status"
                variant={clientConnected ? 'success' : 'error'}
                data-status={clientConnected ? 'connected' : 'disconnected'}
                className="gap-1.5 py-1"
              >
                <span
                  className={cn(
                    'inline-block w-1.5 h-1.5 rounded-full',
                    clientConnected ? 'bg-green-600' : 'bg-danger-900'
                  )}
                />
                {labelConnection}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>Status of the connection to Dawid's backend server (web socket)</TooltipContent>
          </Tooltip>
        </div>

        {deleteConfirmOpen && (
          <DawidConfirm
            open={deleteConfirmOpen}
            onConfirm={() => {
              deleteSession();
              setDeleteConfirmOpen(false);
            }}
            content="Do you really want to delete your session from our server? This is definitive and it will not be possible to recover the data associated with this session."
            cancelText="I've changed my mind, don't delete"
            confirmText="Delete"
            onCancel={() => setDeleteConfirmOpen(false)}
          />
        )}
      </div>
    </TooltipProvider>
  );
}

export const DawidButtonsBar_ = memo(DawidButtonsBar, (prev, next) => (
  prev.expiration === next.expiration &&
  prev.emailAddress === next.emailAddress &&
  prev.mode === next.mode &&
  prev.sessionIdPrivate === next.sessionIdPrivate &&
  prev.sessionIdPublic === next.sessionIdPublic &&
  prev.sessionCreated === next.sessionCreated &&
  prev.clientConnected === next.clientConnected
));

export { DawidButtonsBar_ as DawidButtonsBar };
